var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * MVL expression editor with live diagnostics + autocomplete (CZNA-245).
 *
 * CodeMirror 6 (reusing the @uiw/react-codemirror + @codemirror/* stack already
 * pulled in for the form-config JSON editor). The editor debounces the source
 * and POSTs to `validation/mvl/assist`; the response drives:
 *  - inline red/yellow squigglies via a CodeMirror `linter` source that reads
 *    the latest diagnostics out of a ref (re-linted whenever assist resolves);
 *  - an async `autocompletion` source that POSTs the full document + caret
 *    offset to `validation/mvl/assist` on each popup request and renders the
 *    context-aware completions the backend returns at that caret.
 *
 * The component is controlled (`value` + `onChange`) like JsonCodeEditor and
 * surfaces `onValidityChange(valid)` so the parent form can block save while
 * the expression has compile errors.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import CodeMirror from '@uiw/react-codemirror';
import { linter, lintGutter, forceLinting } from '@codemirror/lint';
import { autocompletion } from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import { useOkapiKy } from '@folio/stripes/core';
import { Loading, MessageBanner } from '@folio/stripes/components';
import { assistMvl } from '../../api/validationRulesService';
import MvlHelpPanel from './MvlHelpPanel';
var DEBOUNCE_MS = 400;
/** Map a backend diagnostic to a CodeMirror Diagnostic (clamped to doc length). */
var toCmDiagnostic = function (d, docLength) {
    var _a, _b;
    var from = Math.max(0, Math.min((_a = d.startOffset) !== null && _a !== void 0 ? _a : 0, docLength));
    var to = Math.max(from, Math.min((_b = d.endOffset) !== null && _b !== void 0 ? _b : from, docLength));
    return {
        from: from,
        to: to === from ? Math.min(from + 1, docLength) : to,
        severity: d.severity === 'ERROR' ? 'error' : 'warning',
        message: d.message,
    };
};
var MvlExpressionEditor = function (_a) {
    var value = _a.value, onChange = _a.onChange, onValidityChange = _a.onValidityChange, _b = _a.readOnly, readOnly = _b === void 0 ? false : _b, testId = _a.testId;
    var intl = useIntl();
    var ky = useOkapiKy();
    var editorRef = useRef(null);
    var debounceRef = useRef(null);
    // Latest diagnostics, read synchronously by the CodeMirror linter source.
    // (Completions are fetched live by the autocompletion override, not cached here.)
    var diagnosticsRef = useRef([]);
    var _c = useState(false), isChecking = _c[0], setIsChecking = _c[1];
    var _d = useState(false), assistError = _d[0], setAssistError = _d[1];
    // Clean up the debounce timer on unmount (ui-czna-development rule).
    useEffect(function () { return function () {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
    }; }, []);
    var runAssist = useCallback(function (expression) {
        var _a;
        if (!expression.trim()) {
            diagnosticsRef.current = [];
            setAssistError(false);
            onValidityChange === null || onValidityChange === void 0 ? void 0 : onValidityChange(true);
            var view = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.view;
            if (view)
                forceLinting(view);
            return;
        }
        setIsChecking(true);
        assistMvl(ky, { expression: expression, caret: expression.length })
            .then(function (res) {
            var _a;
            diagnosticsRef.current = (_a = res.diagnostics) !== null && _a !== void 0 ? _a : [];
            setAssistError(false);
            onValidityChange === null || onValidityChange === void 0 ? void 0 : onValidityChange(res.valid);
        })
            .catch(function () {
            // Assist endpoint unreachable (e.g. CZNA-244 not deployed yet) — don't
            // block the user; surface a soft banner and treat validity as unknown.
            diagnosticsRef.current = [];
            setAssistError(true);
            onValidityChange === null || onValidityChange === void 0 ? void 0 : onValidityChange(true);
        })
            .finally(function () {
            var _a;
            setIsChecking(false);
            var view = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.view;
            if (view)
                forceLinting(view);
        });
    }, 
    // ky intentionally omitted — unstable ref (ui-czna-development rule).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onValidityChange]);
    var handleChange = useCallback(function (next) {
        onChange(next);
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () { return runAssist(next); }, DEBOUNCE_MS);
    }, [onChange, runAssist]);
    // Run an initial assist pass on mount (edit mode loads with an existing expression).
    useEffect(function () {
        runAssist(value);
        // run-once on mount; subsequent runs are debounced via handleChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var extensions = useMemo(function () { return [
        EditorView.lineWrapping,
        lintGutter(),
        linter(function (view) {
            var docLength = view.state.doc.length;
            return diagnosticsRef.current.map(function (d) { return toCmDiagnostic(d, docLength); });
        }),
        autocompletion({
            override: [
                // Context-aware async source: ask the backend for completions at the
                // current caret. The backend returns context-sensitive options (e.g.
                // after `field("` it offers `001 — Číslo autority`), so we cannot use a
                // static list — we POST the full document + caret offset on each
                // (user-driven) popup request. Anchored at the matched word start so the
                // partially-typed token is replaced by the chosen `insert` value.
                function (ctx) { return __awaiter(void 0, void 0, void 0, function () {
                    var word, res, completions, options, _a;
                    var _b;
                    return __generator(this, function (_c) {
                        switch (_c.label) {
                            case 0:
                                word = ctx.matchBefore(/[\w.]*/);
                                if (!word || (word.from === word.to && !ctx.explicit))
                                    return [2 /*return*/, null];
                                _c.label = 1;
                            case 1:
                                _c.trys.push([1, 3, , 4]);
                                return [4 /*yield*/, assistMvl(ky, {
                                        expression: ctx.state.doc.toString(),
                                        caret: ctx.pos,
                                    })];
                            case 2:
                                res = _c.sent();
                                completions = (_b = res.completions) !== null && _b !== void 0 ? _b : [];
                                if (completions.length === 0)
                                    return [2 /*return*/, null];
                                options = completions.map(function (c) { return ({
                                    label: c.label,
                                    apply: c.insert,
                                    type: c.kind,
                                }); });
                                return [2 /*return*/, { from: word.from, options: options }];
                            case 3:
                                _a = _c.sent();
                                // Assist unreachable (e.g. CZNA-244 not deployed) — soft-fail: no
                                // options, no crash. The diagnostics path surfaces the banner.
                                return [2 /*return*/, null];
                            case 4: return [2 /*return*/];
                        }
                    });
                }); },
            ],
        }),
    ]; }, []);
    return (React.createElement("div", { "data-testid": testId },
        React.createElement("div", { style: {
                border: '1px solid #ccc',
                borderRadius: '4px',
                overflow: 'hidden',
            } },
            React.createElement(CodeMirror, { ref: editorRef, value: value, onChange: handleChange, height: "180px", style: { fontSize: '0.875rem' }, extensions: extensions, basicSetup: {
                    lineNumbers: true,
                    highlightActiveLine: true,
                    highlightActiveLineGutter: true,
                    bracketMatching: true,
                    closeBrackets: true,
                    autocompletion: false, // provided via the override extension above
                    tabSize: 2,
                }, readOnly: readOnly })),
        React.createElement("div", { style: { marginTop: '4px', minHeight: '1.25rem', display: 'flex', alignItems: 'center', gap: '6px' } }, isChecking && (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#666', fontSize: '0.8em' } },
            React.createElement(Loading, null),
            intl.formatMessage({ id: 'ui-czna-authorities.validationRules.editor.checking' })))),
        assistError && (React.createElement(MessageBanner, { type: "warning" }, intl.formatMessage({ id: 'ui-czna-authorities.validationRules.editor.assistUnavailable' }))),
        React.createElement("div", { style: { marginTop: '0.5rem' } },
            React.createElement(MvlHelpPanel, null))));
};
export default MvlExpressionEditor;
